import React from 'react';

/**
 * Visual-only overhead / side-panel opening (fixed-sash, fixed-glass, tilt types).
 * Inner-sash types match the Kozijnen professional look: solid stile body, reveal,
 * miter/90° joints, dark glazing bead, then glass + opening dashes.
 */
export function renderOverheadOpeningVisual({
  openingX,
  openingY,
  openingW,
  openingH,
  windowType,
  scale,
  frameThickness,
  frameEdgeStroke,
  glazingBeadFill,
  woodJoint,
  glassFill,
  glassTexturePattern,
  hasInnerFrame,
  renderOverheadFixedSashOutline,
  renderOverheadInnerSashStiles,
  renderOverheadInnerSashBorderlines,
  renderOverheadInnerSashBottomBorders,
  /** Optional solid fill for Vast Panel (falls back to panel-gradient). */
  panelFill,
}) {
  const borderT = 2 * scale;
  const fixedPanelFill = panelFill || 'url(#panel-gradient)';

  if (!hasInnerFrame(windowType)) {
    const glassX = openingX + borderT;
    const glassY = openingY + borderT;
    const glassW = Math.max(0, openingW - borderT * 2);
    const glassH = Math.max(0, openingH - borderT * 2);
    return (
      <g pointerEvents="none">
        {renderOverheadFixedSashOutline(openingX, openingY, openingW, openingH)}
        <rect
          x={glassX}
          y={glassY}
          width={glassW}
          height={glassH}
          fill={windowType === 'fixed-panel' ? fixedPanelFill : glassFill}
        />
        {windowType !== 'fixed-panel' && glassTexturePattern && (
          <rect x={glassX} y={glassY} width={glassW} height={glassH} fill={glassTexturePattern} opacity="1" />
        )}
      </g>
    );
  }

  // Match Kozijnen DrawingBoard proportions so multi-part side panels keep a clear sash body.
  const frameT = Math.max(frameThickness * 0.7, 5);
  const glassInset = Math.max(frameT * 1.25, frameT + 2 * scale);
  const innerX = openingX;
  const innerY = openingY;
  const innerW = openingW;
  const innerH = openingH;
  const glassX = innerX + glassInset;
  const glassY = innerY + glassInset;
  const glassW = Math.max(0, innerW - glassInset * 2);
  const glassH = Math.max(0, innerH - glassInset * 2);
  const miterStroke = frameEdgeStroke || '#D0D4DA';
  const beadT = Math.max(2 * scale, 1.5);

  const tiltLines = (() => {
    if (windowType === 'tilt-only') {
      return (
        <g pointerEvents="none">
          <line x1={glassX} y1={glassY + glassH} x2={glassX + glassW / 2} y2={glassY} stroke="#999999" strokeWidth="3" strokeDasharray="3" />
          <line x1={glassX + glassW} y1={glassY + glassH} x2={glassX + glassW / 2} y2={glassY} stroke="#999999" strokeWidth="3" strokeDasharray="3" />
        </g>
      );
    }
    if (windowType === 'tilt-turn-left') {
      return (
        <g pointerEvents="none">
          <line x1={glassX} y1={glassY + glassH} x2={glassX + glassW / 2} y2={glassY} stroke="#999999" strokeWidth="3" strokeDasharray="3" />
          <line x1={glassX + glassW} y1={glassY + glassH} x2={glassX + glassW / 2} y2={glassY} stroke="#999999" strokeWidth="3" strokeDasharray="3" />
          <line x1={glassX + glassW} y1={glassY} x2={glassX} y2={glassY + glassH / 2} stroke="#999999" strokeWidth="3" strokeDasharray="3" />
          <line x1={glassX + glassW} y1={glassY + glassH} x2={glassX} y2={glassY + glassH / 2} stroke="#999999" strokeWidth="3" strokeDasharray="3" />
        </g>
      );
    }
    if (windowType === 'tilt-turn-right') {
      return (
        <g pointerEvents="none">
          <line x1={glassX} y1={glassY + glassH} x2={glassX + glassW / 2} y2={glassY} stroke="#999999" strokeWidth="3" strokeDasharray="3" />
          <line x1={glassX + glassW} y1={glassY + glassH} x2={glassX + glassW / 2} y2={glassY} stroke="#999999" strokeWidth="3" strokeDasharray="3" />
          <line x1={glassX} y1={glassY} x2={glassX + glassW} y2={glassY + glassH / 2} stroke="#999999" strokeWidth="3" strokeDasharray="3" />
          <line x1={glassX} y1={glassY + glassH} x2={glassX + glassW} y2={glassY + glassH / 2} stroke="#999999" strokeWidth="3" strokeDasharray="3" />
        </g>
      );
    }
    return null;
  })();

  return (
    <g style={{ pointerEvents: 'none' }}>
      {/* Full sash body + reveal (same structure as Kozijnen part sash) */}
      {renderOverheadInnerSashStiles(innerX, innerY, innerW, innerH, frameT)}
      {woodJoint === 'joint-45' ? (
        <>
          <line x1={innerX} y1={innerY} x2={innerX + frameT} y2={innerY + frameT} stroke={miterStroke} strokeWidth="1.2" />
          <line x1={innerX + innerW} y1={innerY} x2={innerX + innerW - frameT} y2={innerY + frameT} stroke={miterStroke} strokeWidth="1.2" />
          <line x1={innerX} y1={innerY + innerH} x2={innerX + frameT} y2={innerY + innerH - frameT} stroke={miterStroke} strokeWidth="1.2" />
          <line x1={innerX + innerW} y1={innerY + innerH} x2={innerX + innerW - frameT} y2={innerY + innerH - frameT} stroke={miterStroke} strokeWidth="1.2" />
        </>
      ) : (
        <>
          <line x1={glassX} y1={innerY + frameT} x2={glassX} y2={innerY} stroke={miterStroke} strokeWidth="1.2" />
          <line x1={glassX + glassW} y1={innerY + frameT} x2={glassX + glassW} y2={innerY} stroke={miterStroke} strokeWidth="1.2" />
          <line x1={glassX} y1={innerY + innerH - frameT} x2={glassX} y2={innerY + innerH} stroke={miterStroke} strokeWidth="1.2" />
          <line x1={glassX + glassW} y1={innerY + innerH - frameT} x2={glassX + glassW} y2={innerY + innerH} stroke={miterStroke} strokeWidth="1.2" />
          <line x1={innerX + frameT} y1={glassY} x2={innerX} y2={glassY} stroke={miterStroke} strokeWidth="1.2" />
          <line x1={innerX + innerW - frameT} y1={glassY} x2={innerX + innerW} y2={glassY} stroke={miterStroke} strokeWidth="1.2" />
          <line x1={innerX + frameT} y1={glassY + glassH} x2={innerX} y2={glassY + glassH} stroke={miterStroke} strokeWidth="1.2" />
          <line x1={innerX + innerW - frameT} y1={glassY + glassH} x2={innerX + innerW} y2={glassY + glassH} stroke={miterStroke} strokeWidth="1.2" />
        </>
      )}
      {renderOverheadInnerSashBorderlines(innerX, innerY, innerW, innerH, frameT)}
      {/* Dark glazing bead around glass */}
      <g pointerEvents="none">
        <rect x={glassX - beadT} y={glassY - beadT} width={glassW + beadT * 2} height={beadT} fill={glazingBeadFill} />
        <rect x={glassX - beadT} y={glassY + glassH} width={glassW + beadT * 2} height={beadT} fill={glazingBeadFill} />
        <rect x={glassX - beadT} y={glassY} width={beadT} height={glassH} fill={glazingBeadFill} />
        <rect x={glassX + glassW} y={glassY} width={beadT} height={glassH} fill={glazingBeadFill} />
      </g>
      <rect x={glassX} y={glassY} width={glassW} height={glassH} fill="url(#glass-gradient)" />
      {glassTexturePattern && (
        <rect x={glassX} y={glassY} width={glassW} height={glassH} fill={glassTexturePattern} opacity="1" />
      )}
      {renderOverheadInnerSashBottomBorders(innerX, innerY, innerW, innerH, frameT)}
      {tiltLines}
    </g>
  );
}
